import React, { useState, useEffect, useMemo } from 'react';
import {
  TableRow,
  TableCell,
  TextField,
  Switch,
  Box,
  Typography,
  Chip,
  Skeleton,
  Collapse,
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormHelperText,
  Slider,
  Tooltip,
} from '@mui/material';
import { getAddressesByCustomer } from '../../../../../api/canvms/CANvmsAPI';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';

const INCIDENT_TRIGGERS = [
  { value: 'person', label: 'Person detected' },
  { value: 'vehicle', label: 'Vehicle detected' },
  { value: 'motion', label: 'Motion detected' },
  { value: 'face', label: 'Face detected' },
  { value: 'animal', label: 'Animal detected' },
  { value: 'intrusion', label: 'Zone intrusion' },
  { value: 'loitering', label: 'Person loitering' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function CameraSettingsRow({
  camera,
  snapshotUrl,
  snapshotLoading,
  onChange,
  modified,
  customers,
  customersLoading,
  onDelete,
}) {
  const [displayName, setDisplayName] = useState(camera.display_name || '');
  const [enabled, setEnabled] = useState(camera.enabled);
  const [expanded, setExpanded] = useState(false);
  const [customerId, setCustomerId] = useState(camera.customer_id || '');
  const [addressId, setAddressId] = useState(camera.address_id || '');
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [autoIncidentEnabled, setAutoIncidentEnabled] = useState(camera.auto_incident_enabled || false);
  const [incidentTriggers, setIncidentTriggers] = useState(() => {
    try {
      return camera.incident_triggers ? JSON.parse(camera.incident_triggers) : [];
    } catch {
      return [];
    }
  });
  const [defaultSeverity, setDefaultSeverity] = useState(camera.default_severity || 'medium');
  const [cooldownSeconds, setCooldownSeconds] = useState(camera.incident_cooldown_seconds || 60);
  const [autoDismissThreshold, setAutoDismissThreshold] = useState(camera.auto_dismiss_threshold ?? 0);

  useEffect(() => {
    setDisplayName(camera.display_name || '');
    setEnabled(camera.enabled);
    setCustomerId(camera.customer_id || '');
    setAddressId(camera.address_id || '');
    setAutoIncidentEnabled(camera.auto_incident_enabled || false);
    try {
      setIncidentTriggers(camera.incident_triggers ? JSON.parse(camera.incident_triggers) : []);
    } catch {
      setIncidentTriggers([]);
    }
    setDefaultSeverity(camera.default_severity || 'medium');
    setCooldownSeconds(camera.incident_cooldown_seconds || 60);
    setAutoDismissThreshold(camera.auto_dismiss_threshold ?? 0);
  }, [camera]);

  useEffect(() => {
    if (customerId) {
      setAddressesLoading(true);
      getAddressesByCustomer(customerId)
        .then((res) => setAddresses(res.data?.addresses || []))
        .catch(() => setAddresses([]))
        .finally(() => setAddressesLoading(false));
    } else {
      setAddresses([]);
    }
  }, [customerId]);

  const triggerValidationError = useMemo(() => {
    if (autoIncidentEnabled && incidentTriggers.length === 0) {
      return 'At least one trigger is required';
    }
    return null;
  }, [autoIncidentEnabled, incidentTriggers]);

  const cooldownValidationError = useMemo(() => {
    if (cooldownSeconds < 10 || cooldownSeconds > 3600) {
      return 'Must be between 10 and 3600 seconds';
    }
    return null;
  }, [cooldownSeconds]);

  const notifyChange = (updates) => {
    onChange(camera.id, {
      display_name: updates.display_name ?? (displayName || null),
      enabled: updates.enabled ?? enabled,
      customer_id: updates.customer_id ?? (customerId || null),
      address_id: updates.address_id ?? (addressId || null),
      auto_incident_enabled: updates.auto_incident_enabled ?? autoIncidentEnabled,
      incident_triggers: updates.incident_triggers ?? incidentTriggers,
      default_severity: updates.default_severity ?? defaultSeverity,
      incident_cooldown_seconds: updates.incident_cooldown_seconds ?? cooldownSeconds,
      auto_dismiss_threshold: updates.auto_dismiss_threshold ?? autoDismissThreshold,
    });
  };

  const handleCustomerChange = (e) => {
    const newValue = e.target.value;
    setCustomerId(newValue);
    setAddressId('');
    notifyChange({ customer_id: newValue || null, address_id: null });
  };

  const handleAddressChange = (e) => {
    const newValue = e.target.value;
    setAddressId(newValue);
    notifyChange({ address_id: newValue || null });
  };

  const handleDisplayNameChange = (e) => {
    const newValue = e.target.value;
    setDisplayName(newValue);
    notifyChange({ display_name: newValue || null });
  };

  const handleEnabledChange = (e) => {
    const newValue = e.target.checked;
    setEnabled(newValue);
    notifyChange({ enabled: newValue });
  };

  const handleAutoIncidentChange = (e) => {
    const newValue = e.target.checked;
    setAutoIncidentEnabled(newValue);
    notifyChange({ auto_incident_enabled: newValue });
  };

  const handleTriggerChange = (trigger) => (e) => {
    const newTriggers = e.target.checked
      ? [...incidentTriggers, trigger]
      : incidentTriggers.filter((t) => t !== trigger);
    setIncidentTriggers(newTriggers);
    notifyChange({ incident_triggers: newTriggers });
  };

  const handleSeverityChange = (e) => {
    const newValue = e.target.value;
    setDefaultSeverity(newValue);
    notifyChange({ default_severity: newValue });
  };

  const handleCooldownChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 60;
    setCooldownSeconds(newValue);
    notifyChange({ incident_cooldown_seconds: newValue });
  };

  const handleThresholdChange = (_, newValue) => {
    setAutoDismissThreshold(newValue);
    notifyChange({ auto_dismiss_threshold: newValue || null });
  };

  const getStatusIcon = (status) => {
    if (status === 'recording' || status === 'watching') {
      return <CheckCircleIcon fontSize="small" color="success" />;
    }
    if (status === 'disabled' || status === 'error') {
      return <ErrorIcon fontSize="small" color="error" />;
    }
    return <HelpIcon fontSize="small" color="disabled" />;
  };

  const originalName = camera.name || camera.stream_id;

  return (
    <>
      <TableRow
        sx={{
          opacity: enabled ? 1 : 0.5,
          bgcolor: modified ? 'action.hover' : 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          '& > td': { borderBottom: expanded ? 'none' : undefined },
        }}
      >
        {/* Thumbnail */}
        <TableCell sx={{ width: 100, p: 1 }}>
          {snapshotLoading ? (
            <Skeleton variant="rectangular" width={80} height={45} />
          ) : snapshotUrl ? (
            <Box
              component="img"
              src={snapshotUrl}
              alt={originalName}
              sx={{
                width: 80,
                height: 45,
                objectFit: 'cover',
                borderRadius: 1,
                bgcolor: 'grey.200',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 45,
                bgcolor: 'grey.300',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No image
              </Typography>
            </Box>
          )}
        </TableCell>

        {/* Original Name */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.5 }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {originalName}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ID: {camera.stream_id}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Display Name (editable) */}
        <TableCell>
          <TextField
            size="small"
            value={displayName}
            onChange={handleDisplayNameChange}
            placeholder={originalName}
            fullWidth
            InputProps={{
              endAdornment: modified && (
                <EditIcon fontSize="small" color="primary" />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </TableCell>

        {/* Status */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getStatusIcon(camera.status)}
            <Chip
              size="small"
              label={camera.status || 'unknown'}
              variant="outlined"
            />
          </Box>
        </TableCell>

        {/* Enabled */}
        <TableCell align="center">
          <Switch
            checked={enabled}
            onChange={handleEnabledChange}
            color="primary"
          />
        </TableCell>

        {/* Delete */}
        <TableCell align="center" sx={{ width: 50 }}>
          <Tooltip title="Pending VMS migration">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expanded Incident Settings */}
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, bgcolor: 'grey.50' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Location Assignment
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Assign this camera to a customer and address for incident dispatch
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={customerId}
                    onChange={handleCustomerChange}
                    label="Customer"
                    disabled={customersLoading}
                  >
                    <MenuItem value=""><em>Inherit from site</em></MenuItem>
                    {(customers || []).map((c) => (
                      <MenuItem key={c.cust_id} value={c.cust_id}>{c.cust_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Address</InputLabel>
                  <Select
                    value={addressId}
                    onChange={handleAddressChange}
                    label="Address"
                    disabled={!customerId || addressesLoading}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {addresses.map((a) => (
                      <MenuItem key={a.address_id} value={a.address_id}>
                        {a.title || a.line1 || `Address #${a.address_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {customerId && !addressesLoading && addresses.length === 0 && (
                    <FormHelperText>No addresses found for this customer</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Incident Settings
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={autoIncidentEnabled}
                    onChange={handleAutoIncidentChange}
                    color="primary"
                  />
                }
                label="Enable auto-incidents"
                sx={{ mb: 2, display: 'block' }}
              />

              <Collapse in={autoIncidentEnabled}>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Trigger on:
                  </Typography>
                  <FormGroup sx={{ mb: 2 }}>
                    {INCIDENT_TRIGGERS.map((trigger) => (
                      <FormControlLabel
                        key={trigger.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={incidentTriggers.includes(trigger.value)}
                            onChange={handleTriggerChange(trigger.value)}
                          />
                        }
                        label={trigger.label}
                      />
                    ))}
                  </FormGroup>
                  {triggerValidationError && (
                    <FormHelperText error sx={{ mb: 2 }}>
                      {triggerValidationError}
                    </FormHelperText>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Default Severity</InputLabel>
                      <Select
                        value={defaultSeverity}
                        onChange={handleSeverityChange}
                        label="Default Severity"
                      >
                        {SEVERITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      type="number"
                      label="Cooldown (seconds)"
                      value={cooldownSeconds}
                      onChange={handleCooldownChange}
                      inputProps={{ min: 10, max: 3600 }}
                      error={!!cooldownValidationError}
                      helperText={cooldownValidationError || 'Prevent duplicate incidents'}
                      sx={{ width: 180 }}
                    />
                  </Box>

                  <Box sx={{ mt: 2, maxWidth: 320 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Auto-dismiss threshold: {autoDismissThreshold > 0 ? `${(autoDismissThreshold * 100).toFixed(0)}%` : 'Off'}
                    </Typography>
                    <Slider
                      value={autoDismissThreshold}
                      onChange={handleThresholdChange}
                      min={0}
                      max={1}
                      step={0.05}
                      marks={[
                        { value: 0, label: 'Off' },
                        { value: 0.5, label: '50%' },
                        { value: 1, label: '100%' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => v > 0 ? `${(v * 100).toFixed(0)}%` : 'Off'}
                      size="small"
                    />
                    <FormHelperText>
                      Events below this confidence are auto-dismissed
                    </FormHelperText>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default CameraSettingsRow;
